"use client";

import type { FavoriteItemDTO, FavoritesPageDTO, User } from "@packages/domain";
import { FavoriteVehicleTypeDTO } from "@packages/domain";
import { useAuth } from "@webmotors/consumer-shared-auth";
import type { InfiniteData } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { favoritesService } from "@/app";
import { useAdobeDataLayer } from "@/hooks/use-adobe-data-layer";
import { queryClient } from "@/providers/query-provider/query-provider.component";
import { FAVORITES_QUERY_KEY, useFavoritesResource } from "@/resources";
import { FavoriteProviderContext } from "./favorite-provider.context";
import type {
  FavoriteProviderProps,
  FavoriteRemoved,
  FavoriteTab,
} from "./favorite-provider.types";

function toUser(
  authUser: { email: string; name: string; sub: string } | null,
  token: string | null
): User | null {
  if (!authUser || !token) return null;
  return {
    id: authUser.sub,
    name: authUser.name,
    email: authUser.email,
    phoneNumber: "",
    authenticationToken: token,
  };
}

type FavoritesInfiniteData = InfiniteData<FavoritesPageDTO> | undefined;

function flattenFavorites(data: FavoritesInfiniteData): FavoriteItemDTO[] {
  return data?.pages.flatMap((page) => page.favorites) ?? [];
}

function totalCount(data: FavoritesInfiniteData): number {
  return data?.pages[0]?.count ?? 0;
}

function removeFromPages(
  data: FavoritesInfiniteData,
  advertiseId: number
): FavoritesInfiniteData {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => {
      const favorites = page.favorites.filter(
        (f) => f.advertiseId !== advertiseId
      );
      if (favorites.length === page.favorites.length) return page;
      return { ...page, favorites, count: Math.max(0, page.count - 1) };
    }),
  };
}

export function FavoriteProvider({
  children,
}: Readonly<FavoriteProviderProps>) {
  const { user: authUser, token, logout, isAuthenticated } = useAuth();
  const { dispatchPageView, dispatchFavoriteOff, isUserDataReady } =
    useAdobeDataLayer();
  const pageViewFired = useRef(false);

  const user = useMemo(() => toUser(authUser, token), [authUser, token]);

  const [activeTab, setActiveTab] = useState<FavoriteTab>("cars");
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);
  const [lastRemoved, setLastRemoved] = useState<FavoriteRemoved | null>(null);

  const carsQuery = useFavoritesResource({
    user,
    vehicleType: FavoriteVehicleTypeDTO.CAR,
    enabled: isAuthenticated,
  });
  const motosQuery = useFavoritesResource({
    user,
    vehicleType: FavoriteVehicleTypeDTO.BIKE,
    enabled: isAuthenticated,
  });

  const carFavorites = useMemo(
    () => flattenFavorites(carsQuery.data),
    [carsQuery.data]
  );
  const motoFavorites = useMemo(
    () => flattenFavorites(motosQuery.data),
    [motosQuery.data]
  );

  const activeQuery = activeTab === "cars" ? carsQuery : motosQuery;
  const currentFavorites = activeTab === "cars" ? carFavorites : motoFavorites;

  const isLoading = activeQuery.isLoading;
  const isLoadingMore = activeQuery.isFetchingNextPage;
  const hasMore = !!activeQuery.hasNextPage;

  const loadMore = useCallback(() => {
    if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      activeQuery.fetchNextPage();
    }
  }, [activeQuery]);

  const favoritesReady = isAuthenticated
    ? carsQuery.isFetchedAfterMount && motosQuery.isFetchedAfterMount
    : true;

  useEffect(() => {
    const userReady = !isAuthenticated || user !== null;
    if (
      favoritesReady &&
      userReady &&
      isUserDataReady &&
      !pageViewFired.current
    ) {
      pageViewFired.current = true;
      const total = totalCount(carsQuery.data) + totalCount(motosQuery.data);
      dispatchPageView(total);
    }
  }, [
    favoritesReady,
    carsQuery.data,
    motosQuery.data,
    dispatchPageView,
    isAuthenticated,
    user,
    isUserDataReady,
  ]);

  const requestRemove = useCallback((advertiseId: number) => {
    setConfirmRemoveId(advertiseId);
  }, []);

  const cancelRemove = useCallback(() => {
    setConfirmRemoveId(null);
  }, []);

  const confirmRemove = useCallback(async () => {
    if (confirmRemoveId === null || !user) return;

    const favorite = [...carFavorites, ...motoFavorites].find(
      (f) => f.advertiseId === confirmRemoveId
    );
    if (!favorite) {
      setConfirmRemoveId(null);
      return;
    }

    setConfirmRemoveId(null);
    setRemovingId(confirmRemoveId);

    const removedGuid = await favoritesService.remove({
      guid: favorite.guid,
      user,
    });

    setRemovingId(null);

    if (!removedGuid) {
      logout();
      return;
    }

    setLastRemoved({
      id: confirmRemoveId,
      guid: favorite.guid,
      vehicleType: favorite.vehicleType,
    });

    dispatchFavoriteOff({
      advertiseId: favorite.advertiseId,
      advertiserId: favorite.advertiserId,
      brand: favorite.brand,
      brandId: favorite.brandId,
      model: favorite.model,
      modelId: favorite.modelId,
      version: favorite.version,
      versionId: favorite.versionId,
    });

    queryClient.setQueryData<InfiniteData<FavoritesPageDTO>>(
      [FAVORITES_QUERY_KEY, favorite.vehicleType, user.id],
      (prev) => removeFromPages(prev, confirmRemoveId)
    );
  }, [
    confirmRemoveId,
    carFavorites,
    motoFavorites,
    logout,
    user,
    dispatchFavoriteOff,
  ]);

  const dismissToast = useCallback(() => {
    setLastRemoved(null);
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      isLoadingMore,
      hasMore,
      loadMore,
      activeTab,
      setActiveTab,
      currentFavorites,
      requestRemove,
      confirmRemoveId,
      cancelRemove,
      confirmRemove,
      removingId,
      lastRemoved,
      dismissToast,
    }),
    [
      isLoading,
      isLoadingMore,
      hasMore,
      loadMore,
      activeTab,
      currentFavorites,
      requestRemove,
      confirmRemoveId,
      cancelRemove,
      confirmRemove,
      removingId,
      lastRemoved,
      dismissToast,
    ]
  );

  return (
    <FavoriteProviderContext.Provider value={value}>
      {children}
    </FavoriteProviderContext.Provider>
  );
}
